import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { organizationService } from '../../services/organizationService';
import { esgService } from '../../services/esgService';
import { fundraiserService } from '../../services/fundraiserService';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';

const OrganizationDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [esgReports, setEsgReports] = useState([]);
  const [fundraisers, setFundraisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
    }
  }, [user?.id]);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const { data: orgsData, error: orgsError } = await organizationService?.getUserOrganizations(user?.id);
      
      if (orgsError) {
        setError('Failed to load organizations');
        return;
      }

      setOrganizations(orgsData || []);
      
      if (orgsData?.length > 0) {
        const firstOrg = orgsData?.[0];
        setSelectedOrganization(firstOrg);
        await loadOrganizationDetails(firstOrg?.id);
      }
    } catch (err) {
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const loadOrganizationDetails = async (orgId) => {
    try {
      const [esgResult, fundraiserResult] = await Promise.all([
        esgService?.getOrganizationESGReports(orgId),
        fundraiserService?.getOrganizationFundraisers(orgId)
      ]);

      if (esgResult?.data) {
        setEsgReports(esgResult?.data);
      }

      if (fundraiserResult?.data) {
        setFundraisers(fundraiserResult?.data);
      }
    } catch (err) {
      console.error('Failed to load organization details:', err);
    }
  };

  const handleOrganizationChange = async (org) => {
    setSelectedOrganization(org);
    await loadOrganizationDetails(org?.id);
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    })?.format(amount || 0);
  };

  const calculateProgress = (current, goal) => {
    if (!goal) return 0;
    return Math.min((current / goal) * 100, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="bg-error/10 border border-error/20 rounded-lg p-8 text-center">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <h2 className="text-xl font-bold text-error mb-2">Error Loading Dashboard</h2>
            <p className="text-error/80 mb-4">{error}</p>
            <Button onClick={loadDashboardData}>Try Again</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8">
        {organizations?.length === 0 ? (
          // No organizations - show setup
          (<div className="text-center py-12">
            <div className="w-24 h-24 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Building2" size={48} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-card-foreground mb-4">
              Create Your Organization Profile
            </h2>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Set up your organization profile to start sharing your ESG impact and managing fundraisers.
            </p>
            <Button 
              onClick={() => navigate('/create-organization')}
              iconName="Plus"
              iconPosition="left"
            >
              Create Organization
            </Button>
          </div>)
        ) : (
          // Show dashboard with data
          (<div className="space-y-8">
            {/* Header with organization selector */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold text-card-foreground mb-2">
                  Organization Dashboard
                </h1>
                <p className="text-muted-foreground">
                  Manage your ESG impact reporting and fundraising activities
                </p>
              </div>
              
              {organizations?.length > 1 && (
                <select
                  value={selectedOrganization?.id || ''}
                  onChange={(e) => {
                    const org = organizations?.find(o => o?.id === e?.target?.value);
                    if (org) handleOrganizationChange(org);
                  }}
                  className="px-4 py-2 border border-border rounded-lg bg-card text-card-foreground"
                >
                  {organizations?.map((org) => (
                    <option key={org?.id} value={org?.id}>
                      {org?.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            {selectedOrganization && (
              <>
                {/* Organization Overview */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      {selectedOrganization?.logo_url ? (
                        <img 
                          src={selectedOrganization?.logo_url} 
                          alt={selectedOrganization?.name}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
                          <Icon name="Building2" size={24} className="text-primary" />
                        </div>
                      )}
                      <div>
                        <h2 className="text-xl font-bold text-card-foreground">
                          {selectedOrganization?.name}
                        </h2>
                        <p className="text-muted-foreground">
                          {selectedOrganization?.industry} • {selectedOrganization?.organization_size}
                        </p>
                        {selectedOrganization?.is_501c3 && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 mt-1">
                            <Icon name="Shield" size={12} className="mr-1" />
                            501(c)(3) Verified
                          </span>
                        )}
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => navigate(`/organization/${selectedOrganization?.id}/edit`)}
                    >
                      <Icon name="Settings" size={16} className="mr-2" />
                      Edit
                    </Button>
                  </div>
                  
                  {selectedOrganization?.description && (
                    <p className="text-muted-foreground mb-4">
                      {selectedOrganization?.description}
                    </p>
                  )}

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">
                        {esgReports?.length || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">ESG Reports</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">
                        {fundraisers?.length || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Fundraisers</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">
                        {formatCurrency(
                          fundraisers?.reduce((sum, f) => sum + (f?.current_amount || 0), 0)
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">Funds Raised</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-primary">
                        {fundraisers?.filter(f => f?.status === 'active')?.length || 0}
                      </div>
                      <div className="text-sm text-muted-foreground">Active Campaigns</div>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8">
                  {/* ESG Reports Section */}
                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-card-foreground">
                        ESG Impact Reports
                      </h3>
                      <Button 
                        size="sm"
                        onClick={() => navigate(`/organization/${selectedOrganization?.id}/esg/create`)}
                      >
                        <Icon name="Plus" size={16} className="mr-2" />
                        Add Report
                      </Button>
                    </div>

                    {esgReports?.length === 0 ? (
                      <div className="text-center py-8">
                        <Icon name="BarChart3" size={48} className="text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground mb-4">No ESG reports yet</p>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/organization/${selectedOrganization?.id}/esg/create`)}
                        >
                          Create Your First Report
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {esgReports?.slice(0, 3)?.map((report) => (
                          <div key={report?.id} className="border border-border rounded-lg p-4">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-medium text-card-foreground">{report?.title}</h4>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                report?.category === 'environmental' ? 'bg-green-100 text-green-800' :
                                report?.category === 'social'? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
                              }`}>
                                {report?.category}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">{report?.description}</p>
                            {report?.current_value && report?.target_value && (
                              <div className="flex items-center text-sm">
                                <span className="text-muted-foreground mr-2">Progress:</span>
                                <div className="flex-1 bg-muted rounded-full h-2 mr-2">
                                  <div 
                                    className="bg-primary h-2 rounded-full" 
                                    style={{ 
                                      width: `${Math.min((report?.current_value / report?.target_value) * 100, 100)}%` 
                                    }}
                                  ></div>
                                </div>
                                <span className="font-medium">
                                  {Math.round((report?.current_value / report?.target_value) * 100)}%
                                </span>
                              </div>
                            )}
                          </div>
                        ))}
                        {esgReports?.length > 3 && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            fullWidth
                            onClick={() => navigate(`/organization/${selectedOrganization?.id}/esg`)}
                          >
                            View All Reports ({esgReports?.length})
                          </Button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Fundraisers Section */}
                  <div className="bg-card border border-border rounded-lg p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-card-foreground">
                        Fundraising Campaigns
                      </h3>
                      <Button 
                        size="sm"
                        onClick={() => navigate(`/organization/${selectedOrganization?.id}/fundraiser/create`)}
                      >
                        <Icon name="Plus" size={16} className="mr-2" />
                        Create Campaign
                      </Button>
                    </div>

                    {fundraisers?.length === 0 ? (
                      <div className="text-center py-8">
                        <Icon name="Target" size={48} className="text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground mb-4">No fundraising campaigns yet</p>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => navigate(`/organization/${selectedOrganization?.id}/fundraiser/create`)}
                        >
                          Start Your First Campaign
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {fundraisers?.slice(0, 3)?.map((fundraiser) => (
                          <div key={fundraiser?.id} className="border border-border rounded-lg p-4">
                            <div className="flex items-start justify-between mb-2">
                              <h4 className="font-medium text-card-foreground">{fundraiser?.title}</h4>
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                fundraiser?.status === 'active' ? 'bg-green-100 text-green-800' :
                                fundraiser?.status === 'completed'? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {fundraiser?.status}
                              </span>
                            </div>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium">
                                {formatCurrency(fundraiser?.current_amount)} raised
                              </span>
                              <span className="text-sm text-muted-foreground">
                                of {formatCurrency(fundraiser?.goal_amount)} goal
                              </span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div 
                                className="bg-primary h-2 rounded-full" 
                                style={{ 
                                  width: `${calculateProgress(fundraiser?.current_amount, fundraiser?.goal_amount)}%` 
                                }}
                              ></div>
                            </div>
                          </div>
                        ))}
                        {fundraisers?.length > 3 && (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            fullWidth
                            onClick={() => navigate(`/organization/${selectedOrganization?.id}/fundraisers`)}
                          >
                            View All Campaigns ({fundraisers?.length})
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>)
        )}
      </div>
    </div>
  );
};

export default OrganizationDashboard;